import React, { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertCircle, Copy, ImageIcon, RefreshCw, X } from "lucide-react";

function getSiteCameraSettingsUrl() {
  if (typeof window === "undefined") return null;
  const site = encodeURIComponent(window.location.origin);
  const ua = navigator.userAgent;
  if (/Edg\//i.test(ua)) return `edge://settings/content/siteDetails?site=${site}`;
  if (/Firefox/i.test(ua)) return "about:preferences#privacy";
  return `chrome://settings/content/siteDetails?site=${site}`;
}

async function copyCameraSettingsLink() {
  const url = getSiteCameraSettingsUrl();
  if (!url) return false;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

function cameraErrorMessage(err) {
  const name = err?.name || "";
  const msg = String(err?.message || err || "");
  if (name === "NotAllowedError" || /permission denied/i.test(msg)) {
    return "permission_denied";
  }
  if (name === "NotFoundError" || /not found/i.test(msg)) {
    return "לא נמצאה מצלמה במכשיר.";
  }
  if (name === "NotReadableError" || /could not start/i.test(msg)) {
    return "המצלמה תפוסה. סגור אפליקציות אחרות שמשתמשות במצלמה.";
  }
  if (
    typeof window !== "undefined" &&
    !window.isSecureContext &&
    !/^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname)
  ) {
    return "גישה למצלמה דורשת localhost או HTTPS — לא כתובת IP ברשת.";
  }
  if (typeof navigator !== "undefined" && !navigator.mediaDevices?.getUserMedia) {
    return "הדפדפן לא תומך במצלמה כאן. השתמש ב-Chrome/Safari בטלפון, או «צלם ברקוד» למטה.";
  }
  return "לא ניתן להפעיל מצלמה.";
}

const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
];

function createBarcodeScanner(elementId) {
  return new Html5Qrcode(elementId, {
    verbose: false,
    formatsToSupport: BARCODE_FORMATS,
    useBarCodeDetectorIfSupported: true,
  });
}

const SCAN_CONFIG = {
  fps: 15,
  disableFlip: false,
  qrbox: (viewfinderWidth, viewfinderHeight) => ({
    width: Math.floor(viewfinderWidth),
    height: Math.floor(viewfinderHeight),
  }),
  videoConstraints: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

async function pickCameraId() {
  try {
    const cameras = await Html5Qrcode.getCameras();
    if (!cameras?.length) return null;

    const byBackLabel = cameras.find((c) => /back|rear|environment|אחור/i.test(c.label || ""));
    if (byBackLabel) return byBackLabel.id;

    const frontByLabel = cameras.find((c) => /front|user|selfie|facetime|קדמ/i.test(c.label || ""));
    if (frontByLabel && cameras.length > 1) {
      const notFront = cameras.find((c) => c.id !== frontByLabel.id);
      if (notFront) return notFront.id;
    }

    // ללא label (נפוץ לפני הרשאה) — ברוב המכשירים האחורית היא האחרונה ברשימה
    if (cameras.length > 1) return cameras[cameras.length - 1].id;

    return cameras[0].id;
  } catch {
    return null;
  }
}

/**
 * מסך מלא לסריקת ברקוד — המצלמה נפתחת מיד כש-open=true (דרישת user gesture מהכפתור שפתח).
 */
export default function BarcodeScanDialog({
  open,
  onOpenChange,
  onScan,
  readerId = "barcode-scan-reader",
  manualValue = "",
  onManualChange,
}) {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoStartedRef = useRef(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [photoScanning, setPhotoScanning] = useState(false);
  const [settingsCopied, setSettingsCopied] = useState(false);

  const cleanupScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner?.isScanning) {
      try {
        await scanner.stop();
      } catch (_) {}
    }
  }, []);

  const stopScanner = useCallback(async () => {
    await cleanupScanner();
    setCameraActive(false);
  }, [cleanupScanner]);

  const close = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) {
      stopScanner();
      setCameraError(null);
      setStarting(false);
      setPhotoScanning(false);
      setSettingsCopied(false);
      autoStartedRef.current = false;
    }
  }, [open, stopScanner]);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const applyScan = useCallback(
    (code) => {
      const trimmed = String(code || "").trim();
      if (trimmed) onScan?.(trimmed);
      onOpenChange?.(false);
    },
    [onOpenChange, onScan]
  );

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("no_media");
      return;
    }

    setCameraError(null);
    setStarting(true);
    setCameraActive(true);

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      await cleanupScanner();

      const element = document.getElementById(readerId);
      if (!element) throw new Error("reader element missing");

      const html5QrCode = createBarcodeScanner(readerId);
      scannerRef.current = html5QrCode;

      const onSuccess = (decodedText) => {
        applyScan(decodedText);
        stopScanner();
      };

      const cameraId = await pickCameraId();
      const attempts = [
        { facingMode: { exact: "environment" } },
        { facingMode: "environment" },
        ...(cameraId ? [cameraId] : []),
        { facingMode: "user" },
      ];

      let lastErr;
      for (const camera of attempts) {
        try {
          await html5QrCode.start(camera, SCAN_CONFIG, onSuccess);
          setCameraError(null);
          return;
        } catch (err) {
          lastErr = err;
          if (html5QrCode.isScanning) {
            try {
              await html5QrCode.stop();
            } catch (_) {}
          }
        }
      }

      throw lastErr || new Error("camera start failed");
    } catch (err) {
      console.error("Scanner start error:", err);
      await stopScanner();
      setCameraError(cameraErrorMessage(err));
    } finally {
      setStarting(false);
    }
  }, [applyScan, cleanupScanner, readerId, stopScanner]);

  useEffect(() => {
    if (!open || autoStartedRef.current) return;
    autoStartedRef.current = true;
    startCamera();
  }, [open, startCamera]);

  const handlePhotoFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      setPhotoScanning(true);
      setCameraError(null);
      try {
        const scanner = createBarcodeScanner(`${readerId}-file`);
        const code = await scanner.scanFile(file, false);
        applyScan(code);
      } catch (err) {
        console.error("Photo scan error:", err);
        setCameraError("לא זוהה ברקוד בתמונה. נסה שוב או הקלד ידנית.");
      } finally {
        setPhotoScanning(false);
      }
    },
    [applyScan, readerId]
  );

  if (!open) return null;

  const showReader = starting || cameraActive;

  return (
    <div className="fixed inset-0 z-[300] flex flex-col bg-black" dir="rtl">
      <div id={`${readerId}-file`} className="hidden" aria-hidden />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoFile}
      />

      <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-black/60 text-white">
        <button
          type="button"
          onClick={close}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          aria-label="סגור"
        >
          <X className="h-5 w-5" />
        </button>
        <p className="text-sm font-medium">סריקת ברקוד</p>
        <div className="w-10" aria-hidden />
      </div>

      <div className="relative flex-1 min-h-0">
        <div
          id={readerId}
          className={
            showReader
              ? "absolute inset-0 [&_video]:!h-full [&_video]:!w-full [&_video]:!object-cover [&_img]:!hidden"
              : "hidden"
          }
        />

        {!showReader && !cameraError && !photoScanning ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
            פותח מצלמה…
          </div>
        ) : null}

        {photoScanning ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
            מזהה ברקוד בתמונה…
          </div>
        ) : null}

        {cameraActive ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-24 px-6 text-center">
            <p className="rounded-lg bg-black/50 px-4 py-2 text-sm text-white">
              החזק את הברקוד אופקית, במרחק ~15 ס״מ
            </p>
          </div>
        ) : null}
      </div>

      <div className="relative z-10 space-y-3 bg-black/80 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        {cameraError ? (
          <div className="rounded-lg border border-red-400/40 bg-red-950/80 p-3 text-sm text-red-100 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <p>
                {cameraError === "permission_denied"
                  ? "לחץ על הסמל ליד הכתובת למעלה → מצלמה → אפשר → רענן."
                  : cameraError === "no_media"
                    ? "המצלמה לא זמינה כאן."
                    : cameraError}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {cameraError === "permission_denied" ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-red-300/50 bg-transparent text-white hover:bg-white/10"
                    onClick={async () => {
                      const ok = await copyCameraSettingsLink();
                      setSettingsCopied(ok);
                    }}
                  >
                    <Copy className="w-4 h-4 ml-2" />
                    {settingsCopied ? "הקישור הועתק — הדבק בשורת Chrome" : "העתק קישור להגדרות"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-red-300/50 bg-transparent text-white hover:bg-white/10"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw className="w-4 h-4 ml-2" />
                    רענן דף
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-red-300/50 bg-transparent text-white hover:bg-white/10"
                  onClick={startCamera}
                >
                  נסה שוב
                </Button>
              )}
            </div>
          </div>
        ) : null}

        <Button
          type="button"
          variant="secondary"
          className="w-full bg-white/15 text-white hover:bg-white/25 border-0"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon className="w-4 h-4 ml-2" />
          צלם / בחר תמונת ברקוד
        </Button>

        {onManualChange ? (
          <Input
            placeholder="או הקלד ברקוד ידנית..."
            value={manualValue}
            onChange={(e) => onManualChange(e.target.value)}
            className="h-10 text-right bg-white/10 border-white/20 text-white placeholder:text-white/50"
          />
        ) : null}
      </div>
    </div>
  );
}
